import {
  forwardRef,
  PointerEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react'
import './SignaturePad.css'
import { cx } from '@/utils/cx'

export interface SignaturePadHandle {
  clear: () => void
  isEmpty: () => boolean
  toDataURL: (type?: string) => string | null
}

interface SignaturePadProps {
  className?: string
  penColor?: string
  penWidth?: number
  ariaLabel?: string
  onChange?: (isEmpty: boolean) => void
}

interface Point {
  x: number
  y: number
}

export const SignaturePad = forwardRef<SignaturePadHandle, SignaturePadProps>(
  ({ className, penColor, penWidth = 2.5, ariaLabel = 'Signature pad', onChange }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const isDrawingRef = useRef(false)
    const isEmptyRef = useRef(true)
    const lastPointRef = useRef<Point | null>(null)
    const penColorRef = useRef<string | null>(penColor ?? null)

    const getContext = useCallback(() => canvasRef.current?.getContext('2d') ?? null, [])

    useEffect(() => {
      penColorRef.current = penColor ?? null
    }, [penColor])

    const getPenColor = useCallback(() => {
      if (penColorRef.current) return penColorRef.current
      const canvas = canvasRef.current
      const resolved = canvas ? getComputedStyle(canvas).color || '#000' : '#000'
      penColorRef.current = resolved
      return resolved
    }, [])

    const resizeCanvas = useCallback(() => {
      const canvas = canvasRef.current
      const ctx = getContext()
      if (!canvas || !ctx) return

      const ratio = window.devicePixelRatio || 1
      const { width, height } = canvas.getBoundingClientRect()

      const nextWidth = Math.round(width * ratio)
      const nextHeight = Math.round(height * ratio)

      if (nextWidth === 0 || nextHeight === 0) return

      if (canvas.width === nextWidth && canvas.height === nextHeight) return

      const snapshot = isEmptyRef.current ? null : canvas.toDataURL('image/png')
      canvas.width = nextWidth
      canvas.height = nextHeight

      isDrawingRef.current = false
      lastPointRef.current = null

      ctx.scale(ratio, ratio)
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'

      if (snapshot) {
        const img = new Image()
        img.onload = () => {
          ctx.drawImage(img, 0, 0, nextWidth / ratio, nextHeight / ratio)
        }
        img.src = snapshot
        isEmptyRef.current = false
        onChange?.(false)
      } else {
        isEmptyRef.current = true
        onChange?.(true)
      }
    }, [getContext, onChange])

    useEffect(() => {
      resizeCanvas()

      const canvas = canvasRef.current

      if (canvas && typeof ResizeObserver !== 'undefined') {
        const observer = new ResizeObserver(resizeCanvas)
        observer.observe(canvas)
        return () => observer.disconnect()
      }

      window.addEventListener('resize', resizeCanvas)
      return () => window.removeEventListener('resize', resizeCanvas)
    }, [resizeCanvas])

    const getPoint = (event: PointerEvent<HTMLCanvasElement>): Point => {
      const rect = canvasRef.current!.getBoundingClientRect()
      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      }
    }

    const handlePointerDown = (event: PointerEvent<HTMLCanvasElement>) => {
      event.preventDefault()
      const ctx = getContext()
      if (!ctx) return

      canvasRef.current?.setPointerCapture(event.pointerId)
      isDrawingRef.current = true

      const point = getPoint(event)
      lastPointRef.current = point

      ctx.beginPath()
      ctx.fillStyle = getPenColor()
      ctx.arc(point.x, point.y, penWidth / 2, 0, Math.PI * 2)
      ctx.fill()

      isEmptyRef.current = false
    }

    const handlePointerMove = (event: PointerEvent<HTMLCanvasElement>) => {
      if (!isDrawingRef.current) return
      event.preventDefault()
      const ctx = getContext()
      const last = lastPointRef.current
      if (!ctx || !last) return

      const point = getPoint(event)

      ctx.beginPath()
      ctx.strokeStyle = getPenColor()
      ctx.lineWidth = penWidth
      ctx.moveTo(last.x, last.y)
      ctx.lineTo(point.x, point.y)
      ctx.stroke()

      lastPointRef.current = point
      if (isEmptyRef.current) {
        isEmptyRef.current = false
      }
    }

    const endStroke = (event: PointerEvent<HTMLCanvasElement>) => {
      if (!isDrawingRef.current) return
      isDrawingRef.current = false
      lastPointRef.current = null

      if (canvasRef.current?.hasPointerCapture(event.pointerId)) {
        canvasRef.current.releasePointerCapture(event.pointerId)
      }
      onChange?.(isEmptyRef.current)
    }

    const clear = useCallback(() => {
      const canvas = canvasRef.current
      const ctx = getContext()
      if (!canvas || !ctx) return
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      isEmptyRef.current = true
      onChange?.(true)
    }, [getContext, onChange])

    useImperativeHandle(
      ref,
      () => ({
        clear,
        isEmpty: () => isEmptyRef.current,
        toDataURL: (type = 'image/png') =>
          isEmptyRef.current ? null : (canvasRef.current?.toDataURL(type) ?? null),
      }),
      [clear]
    )

    return (
      <div className={cx('signature-pad__wrapper', className)}>
        <canvas
          ref={canvasRef}
          className="signature-pad"
          style={penColor ? undefined : { color: 'var(--text-color, #000)' }}
          aria-label={ariaLabel}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endStroke}
          onPointerLeave={endStroke}
          onPointerCancel={endStroke}
          onContextMenu={(event) => event.preventDefault()}
        />
      </div>
    )
  }
)
